import { useSyncExternalStore } from 'react'

/** Read-aloud playback speed preference — device-local, like the voice choice. */
export const TTS_SPEEDS = [0.75, 1, 1.25, 1.75] as const
export type TtsSpeed = (typeof TTS_SPEEDS)[number]

export const TTS_SPEED_STORAGE_KEY = 'hub-tts-speed'
export const DEFAULT_TTS_SPEED: TtsSpeed = 1

function isTtsSpeed(value: number): value is TtsSpeed {
  return (TTS_SPEEDS as readonly number[]).includes(value)
}

/**
 * Single source of truth: this module (localStorage + an in-memory copy for
 * when storage is unavailable). Every UI that shows it (today the settings
 * dialog's Profil section) reads it through `useTtsSpeed`, so they always agree.
 */
let sessionSpeed: TtsSpeed | null = null
const listeners = new Set<() => void>()

export function getTtsSpeed(): TtsSpeed {
  if (sessionSpeed !== null) return sessionSpeed
  try {
    const raw = window.localStorage.getItem(TTS_SPEED_STORAGE_KEY)
    const parsed = raw === null ? Number.NaN : Number(raw)
    return isTtsSpeed(parsed) ? parsed : DEFAULT_TTS_SPEED
  } catch {
    return DEFAULT_TTS_SPEED
  }
}

/** Audio elements currently bound to the preference — they follow speed changes live. */
const boundAudio = new Set<HTMLAudioElement>()

export function setTtsSpeed(speed: TtsSpeed): void {
  try {
    window.localStorage.setItem(TTS_SPEED_STORAGE_KEY, String(speed))
    sessionSpeed = null
  } catch {
    // Storage unavailable: keep the choice in memory for this session.
    sessionSpeed = speed
  }
  for (const audio of boundAudio) audio.playbackRate = speed
  for (const listener of listeners) listener()
}

export function subscribeTtsSpeed(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The current speed + its setter, re-rendering on every change. */
export function useTtsSpeed(): [TtsSpeed, (speed: TtsSpeed) => void] {
  const speed = useSyncExternalStore(subscribeTtsSpeed, getTtsSpeed, () => DEFAULT_TTS_SPEED)
  return [speed, setTtsSpeed]
}

/**
 * Apply the current speed to `audio` and keep it in sync while bound, so a
 * change from the settings menu takes effect on playback already in progress.
 */
export function bindTtsSpeed(audio: HTMLAudioElement): void {
  audio.preservesPitch = true
  audio.playbackRate = getTtsSpeed()
  boundAudio.add(audio)
}

export function unbindTtsSpeed(audio: HTMLAudioElement): void {
  boundAudio.delete(audio)
}

/** `1.25` → `"1.25x"`, `1` → `"1.0x"`. */
export function formatTtsSpeed(speed: TtsSpeed): string {
  return speed === 1 ? '1.0x' : `${speed}x`
}
